<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      addCanned: {
        showLoading: false,
        message: '',
      },
      files: [],
      isUploadingFile: false,
      show: true,
    };
  },
  validations() {
    return {
      shortCode: {
        required,
        minLength: minLength(2),
      },
      content: {
        required: value =>
          !!String(value || '').trim() || this.files.length > 0,
      },
    };
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.files = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    openFilePicker() {
      this.$refs.fileInput?.click();
    },
    async uploadCannedFile(event) {
      const selectedFiles = Array.from(event.target.files || []);
      if (!selectedFiles.length) return;

      this.isUploadingFile = true;
      try {
        const uploadedFiles = await Promise.all(
          selectedFiles.map(async file => {
            const { fileUrl, blobId } = await uploadFile(file);
            return {
              id: blobId,
              blob_id: blobId,
              blob_signed_id: blobId,
              file_url: fileUrl,
              filename: file.name,
              file_type: file.type,
              byte_size: file.size,
            };
          })
        );
        this.files = [...this.files, ...uploadedFiles];
        this.v$.content.$touch();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          this.$t('CANNED_MGMT.ATTACHMENTS.ERROR');
        useAlert(errorMessage);
      } finally {
        this.isUploadingFile = false;
        event.target.value = '';
      }
    },
    removeFile(fileToRemove) {
      this.files = this.files.filter(file => file.id !== fileToRemove.id);
      this.v$.content.$touch();
    },
    isImage(file) {
      return file.file_type?.includes('image');
    },
    addCannedResponse() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      // Show loading on button
      this.addCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          blob_ids: this.files.map(file => file.blob_signed_id),
        })
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="w-full">
          <input
            ref="fileInput"
            type="file"
            multiple
            class="hidden"
            accept="image/*"
            @change="uploadCannedFile"
          />
          <div class="flex items-center gap-2 py-2">
            <NextButton
              type="button"
              slate
              faded
              icon="i-lucide-image-plus"
              :label="$t('CANNED_MGMT.ATTACHMENTS.ADD')"
              :is-loading="isUploadingFile"
              @click.prevent="openFilePicker"
            />
          </div>
          <div v-if="files.length" class="flex flex-wrap gap-2 pb-2">
            <div
              v-for="file in files"
              :key="file.id"
              class="flex items-center gap-2 max-w-full px-2 py-1 rounded-md bg-n-slate-3"
            >
              <img
                v-if="isImage(file)"
                :src="file.file_url"
                :alt="file.filename"
                class="object-cover rounded-sm size-8"
              />
              <span v-else class="i-lucide-file text-lg" />
              <span class="text-sm truncate max-w-48 text-n-slate-12">
                {{ file.filename }}
              </span>
              <NextButton
                type="button"
                ghost
                slate
                xs
                icon="i-lucide-x"
                @click.prevent="removeFile(file)"
              />
            </div>
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              isUploadingFile ||
              addCanned.showLoading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
